import { TFunction } from "@impero-com/common/utils/i18n";
import { timeToDueDateFilterSchema } from "@impero-com/ui/domain";
import { z } from "zod";

import { Storage } from "utils/storage";

const overdueRangeSchema = timeToDueDateFilterSchema.extend({
    direction: z.literal("overdue"),
});
const rangesSchema = z.object({
    under: overdueRangeSchema.transform((range) => ({
        ...range,
        comparison: { ...range.comparison, type: "atMost" as const },
    })),
    atLeast: overdueRangeSchema.transform((range) => ({
        ...range,
        comparison: { ...range.comparison, type: "atLeast" as const },
    })),
});
export type OverdueRanges = z.infer<typeof rangesSchema>;
export type OverdueRange = z.infer<typeof overdueRangeSchema>;

export const DEFAULT_OVERDUE_RANGES: OverdueRanges = {
    under: {
        direction: "overdue",
        unit: "days",
        comparison: { type: "atMost", value: 6 },
    },
    atLeast: {
        direction: "overdue",
        unit: "days",
        comparison: { type: "atLeast", value: 7 },
    },
};

export const overdueRangesStorage = new Storage<
    "localStorage",
    "controlHealth",
    "overdueRanges",
    OverdueRanges
>({
    type: "localStorage",
    namespace: "controlHealth",
    codecs: {
        encodeKey: (key) => key,
        encodeValue: JSON.stringify,
        decodeValue: (value) => {
            const saved: unknown = JSON.parse(value);
            return rangesSchema.parse(
                Array.isArray(saved)
                    ? { under: saved[0], atLeast: saved[1] }
                    : saved
            );
        },
    },
});

export const statusVisibilityStorage = new Storage<
    "localStorage",
    "controlHealth",
    "statusColumnVisibility",
    Record<string, boolean>
>({
    type: "localStorage",
    namespace: "controlHealth",
    codecs: {
        encodeKey: (key) => key,
        encodeValue: JSON.stringify,
        decodeValue: (value) => {
            const { underSevenDays, sevenDaysOrMore, ...visibility } = z
                .record(z.string(), z.boolean())
                .parse(JSON.parse(value));
            return {
                ...(underSevenDays === undefined
                    ? {}
                    : { under: underSevenDays }),
                ...(sevenDaysOrMore === undefined
                    ? {}
                    : { atLeast: sevenDaysOrMore }),
                ...visibility,
            };
        },
    },
});

export function overdueRangeHours(range: OverdueRange): number {
    const hoursPerUnit = range.unit === "days" ? 24 : 1;
    // The drill-down's inclusive whole-unit bound becomes an exclusive threshold.
    return range.comparison.type === "atMost"
        ? (range.comparison.value + 1) * hoursPerUnit
        : range.comparison.value * hoursPerUnit;
}

export function overdueColumnLabel(range: OverdueRange, t: TFunction): string {
    const under = range.comparison.type === "atMost";
    const duration = t(
        range.unit === "days"
            ? "ControlActivitiesList_TimeToDueDate_Days"
            : "ControlActivitiesList_TimeToDueDate_Hours",
        { count: range.comparison.value + (under ? 1 : 0) }
    );
    return t(
        under
            ? "ControlHealth_OverdueColumn_Under"
            : "ControlHealth_OverdueColumn_Over",
        { duration }
    );
}
